import { useMemo } from 'react'

import { Anchor } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useTranslate } from 'react-admin'
import {
  ScamExportBodyApiDTO,
  ScamExportPeriodApiDTO,
  ScamExportRowApiDTO,
} from '../../../codegen/generated/Api.schemas'
import { useExportsContext } from '../../../contexts/ExportsContext'
import { API } from '../../../helpers/Api.helper'
import { DateHelper } from '../../../helpers/Date.helper'
import { LXLoader } from '../../LXLoader'
import { LXTable, LXTableColumn } from '../../LXTable/LXTable'

type LXSCAMExportsTableType = Record<keyof ScamExportRowApiDTO, unknown> & {
  id: string
  youtubeChannel: JSX.Element
  videoDuration: string
}

export const SCAMExportsPreview = () => {
  const t = useTranslate()
  const context = useExportsContext()

  const scamExportsQuery = useQuery<ScamExportRowApiDTO[]>({
    queryKey: ['scam-export', context.scam.filters, context.scam.period],
    queryFn: async () => {
      const response = await API.post<ScamExportBodyApiDTO, ScamExportRowApiDTO[]>(
        '/api/scam-export/preview',
        {
          filters: context.scam.filters,
          period: context.scam.period,
        },
      )

      return response.data
    },
  })

  const columns: LXTableColumn<LXSCAMExportsTableType>[] = useMemo(
    () => [
      { key: 'artworkName', value: t('pages.artworks.artworkName'), size: 'md' },
      { key: 'assetId', value: t('_generics.assetId') },
      {
        key: 'youtubeChannel',
        value: t('_generics.youtubeChannel'),
        type: 'component',
        size: 'md',
      },
      { key: 'youtubeUrl', value: t('_generics.youtubeUrl') },

      {
        key: 'originalArtworkCode',
        value: t('pages.artworks.originalArtworkCode'),
        type: 'component',
      },
      { key: 'ISAN', value: 'ISAN' },
      { key: 'videoDuration', value: t('pages.exports.videoDuration') },
    ],
    [t],
  )

  const scamData = useMemo(
    () =>
      (scamExportsQuery.data ?? [])?.map(se => ({
        ...se,
        id: se.assetId,
        youtubeChannel: (
          <Anchor href={`https://www.youtube.com/channel/${se?.channelId}`} target="blank">
            {se.channelName}
          </Anchor>
        ),
        videoDuration: se.videoDurationInSeconds
          ? DateHelper.getDigitalTime(se.videoDurationInSeconds, 's')
          : '',
      })),
    [scamExportsQuery.data],
  )

  if (scamExportsQuery.isLoading) {
    return <LXLoader size="md" />
  }

  return <LXTable<LXSCAMExportsTableType> items={scamData} columns={columns} />
}
